import CustomerLookupCombobox from "@/components/form/CustomerLook";
import { CustomerItem } from "@/components/sale-page/useCustomerSearch";
import { formatAsCurrency } from "@/data/amounts";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Fragment, useEffect, useState } from "react";
import { CurrenciesWithGuinea, Lot } from "../../../../types";

interface ResellDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  lots: Lot[];
  marketId: string;
}

interface LotResellData {
  lotId: string;
  newPriceInCents: number;
}

export default function ResellDialog(props: ResellDialogProps) {
  let { open, setOpen, lots, marketId } = props;

  return (
    <Transition show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[60]"
        onClose={() => setOpen(false)}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-martEye-700 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-start justify-center p-4 text-center sm:items-start sm:p-0 sm:pt-12 sm:pb-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="relative transform rounded-lg bg-white text-left text-martEye-700 shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl">
                <div className="px-4 pb-4 pt-5 sm:p-6">
                  <div className="flex items-start justify-between">
                    <DialogTitle
                      as="h3"
                      className="inline-flex items-center gap-x-1.5 text-xl font-bold"
                    >
                      Resell {lots.length} {lots.length === 1 ? "Lot" : "Lots"}
                    </DialogTitle>

                    <div className="ml-3 flex h-7 items-center">
                      <button
                        type="button"
                        className="rounded-full bg-martEye-500 p-1 text-martEye-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-martEye-400 hover:bg-martEye-400"
                        onClick={() => setOpen(false)}
                      >
                        <span className="sr-only">Close panel</span>
                        <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                    </div>
                  </div>

                  <ResellDialogInner
                    key={`${open}`}
                    open={open}
                    setOpen={setOpen}
                    lots={lots}
                    marketId={marketId}
                  />
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

function ResellDialogInner(props: {
  open: boolean;
  setOpen: (open: boolean) => void;
  lots: Lot[];
  marketId: string;
}) {
  let { open, setOpen, lots } = props;
  let [selectedCustomer, setSelectedCustomer] = useState<CustomerItem | null>(
    null
  );
  let [resellPrices, setResellPrices] = useState<Record<string, number>>({});

  // Initialize resell prices with current prices when dialog opens
  useEffect(() => {
    if (open && lots.length > 0) {
      const initialPrices: Record<string, number> = {};
      lots.forEach((lot) => {
        const currentPrice = lot.generated?.totalValueInCents || 0;
        initialPrices[lot.id] = currentPrice;
      });
      setResellPrices(initialPrices);
    }
  }, [open, lots]);

  const handlePriceChange = (lotId: string, newPrice: string) => {
    const priceInCents = Math.round(parseFloat(newPrice || "0") * 100);
    setResellPrices((prev) => ({
      ...prev,
      [lotId]: priceInCents,
    }));
  };

  const getResellPrice = (lotId: string): number => {
    return resellPrices[lotId] || 0;
  };

  const getPriceDifference = (
    originalPriceInCents: number,
    newPriceInCents: number
  ): number => {
    return newPriceInCents - originalPriceInCents;
  };

  const formatPriceDifference = (
    difference: number,
    currency: CurrenciesWithGuinea
  ): string => {
    const sign = difference >= 0 ? "+" : "";
    return `${sign}${formatAsCurrency(currency, Math.abs(difference))}`;
  };

  return (
    <>
      {/* Customer Selection */}
      <div className="mt-4">
        <CustomerLookupCombobox
          label="Resell to Customer"
          customerType="buyer"
          value={selectedCustomer}
          onChange={setSelectedCustomer}
          disabled={false}
          disallowCasual={true}
        />
      </div>

      {/* Lots Table */}
      <div className="mt-6">
        <div className="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900"
                >
                  Lot
                </th>
                <th
                  scope="col"
                  className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                >
                  Description
                </th>
                <th
                  scope="col"
                  className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900"
                >
                  Qty
                </th>
                <th
                  scope="col"
                  className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900"
                >
                  Original Price
                </th>
                <th
                  scope="col"
                  className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900"
                >
                  Resell Price
                </th>
                <th
                  scope="col"
                  className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900"
                >
                  Difference
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {lots.map((lot) => {
                const originalPrice = lot.generated?.totalValueInCents || 0;
                const resellPrice = getResellPrice(lot.id);
                const difference = getPriceDifference(
                  originalPrice,
                  resellPrice
                );
                const currency = lot.currency as CurrenciesWithGuinea;

                return (
                  <tr key={lot.id} className="odd:bg-gray-50">
                    <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm font-medium text-gray-900">
                      {lot.lotNumber}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-500 max-w-xs truncate">
                      {lot.generated?.description}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500 text-center">
                      {lot.generated?.countOfItems}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500 text-right">
                      {formatAsCurrency(currency, originalPrice)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500 text-right">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={(getResellPrice(lot.id) / 100).toFixed(2)}
                        className="w-24 rounded-md border-gray-300 text-right text-sm focus:border-martEye-500 focus:ring-martEye-500"
                        onChange={(e) =>
                          handlePriceChange(lot.id, e.target.value)
                        }
                      />
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-sm text-right">
                      <span className="font-medium text-gray-700">
                        {formatPriceDifference(difference, currency)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mt-6 flex justify-end gap-3">
        <button
          type="button"
          className="inline-flex justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          onClick={() => setOpen(false)}
        >
          Cancel
        </button>
        <button
          type="button"
          className="inline-flex justify-center rounded-md bg-martEye-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-martEye-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-martEye-600 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={() => {
            // TODO: Implement resell functionality
            console.log(
              "Resell lots:",
              lots,
              "to customer:",
              selectedCustomer,
              "with prices:",
              resellPrices
            );
            setOpen(false);
          }}
          disabled={!selectedCustomer}
        >
          Resell Lots
        </button>
      </div>
    </>
  );
}
